import { allAmenities } from '@components/contants';
import { closeBackEntry, pushBackEntry } from '@components/guest/backstack';
import { TAssetGroup, TAssetGroupAvailability, TBookingRequirement, TPricingPlan,TStore } from "@components/types";
import { getCancellationSummary, getOccupancyRules, getOfferedPlans, getStayPricing, pricingFromQuote, sanityIoImageLoader, withRoomGst } from '@core/utils';
import * as LucideIcons from "lucide-react";
import { CalendarX2, Check, CheckCircle2, ChevronDown, Loader, Minus, Plus, Users, XCircle } from "lucide-react";

// Whole rupees only (rounded, not truncated). Where a total is split, the second part is derived
// from the rounded figures so the lines on the card still add up.
const formatInr = (x: number) =>
    Math.round(x || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
import Image from "next/image";
import React, { useEffect, useMemo, useRef, useState } from "react";

const AMENITY_MAX_VISIBLE = 5;

const AmenitiesList: React.FC<{ amenityIds?: number[] }> = ({ amenityIds }) => {
    const [showAll, setShowAll] = useState(false);

    const matched = useMemo(
        () => (amenityIds ?? [])
            .map((id) => allAmenities.find((a) => a.id === id))
            .filter(Boolean),
        [amenityIds]
    );

    if (!matched.length) return null;

    const visible = showAll ? matched : matched.slice(0, AMENITY_MAX_VISIBLE);
    const overflow = matched.length - AMENITY_MAX_VISIBLE;

    return (
        <ul className="flex flex-wrap gap-x-3 gap-y-1.5 text-sm text-neutral-500" aria-label="Room amenities">
            {visible.map((amenity) => {
                const Icon = (LucideIcons as any)[amenity.icon] as React.FC<{ size?: number }> | undefined;
                return (
                    <li key={amenity.id} className="flex items-center gap-1" title={amenity.name}>
                        {Icon
                            ? <Icon size={12} />
                            : <span className="w-1.5 h-1.5 rounded-full bg-neutral-300 inline-block" />
                        }
                        {amenity.name}
                    </li>
                );
            })}
            {!showAll && overflow > 0 && (
                <li>
                    <button
                        onClick={() => setShowAll(true)}
                        className="text-xs text-orange-500 font-semibold hover:underline bg-transparent border-none cursor-pointer p-0"
                        style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}
                    >
                        +{overflow} more
                    </button>
                </li>
            )}
        </ul>
    );
};

// Modern custom dropdown for meal-plan selection — replaces AntD's Select, which
// renders poorly (tiny popup, no room for price/description) inside a compact
// room card. Closes on outside click / Escape; selection shows name + price
// inline so the trigger stays informative even collapsed.
export const MealPlanDropdown: React.FC<{
    plans: TPricingPlan[];
    selected: TPricingPlan | null;
    getPrice: (p: TPricingPlan) => number | undefined;
    onSelect: (p: TPricingPlan) => void;
    // Lets the parent lift its own stacking layer while the list is open (see article below).
    onOpenChange?: (open: boolean) => void;
}> = ({ plans, selected, getPrice, onSelect, onOpenChange }) => {
    const [open, setOpen] = useState(false);
    useEffect(() => { onOpenChange?.(open); }, [open]);
    const rootRef = useRef<HTMLDivElement>(null);

    // Opening the dropdown pushes a back-stack entry so the mobile/hardware
    // back button closes it first instead of skipping past to the underlying
    // screen (matches the rest of the guest webview's stacked navigation).
    const close = () => setOpen(false);
    const toggle = () => {
        if (open) { closeBackEntry(); return; }
        pushBackEntry(close);
        setOpen(true);
    };

    useEffect(() => {
        if (!open) return;
        const onClickOutside = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) closeBackEntry();
        };
        const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') closeBackEntry(); };
        document.addEventListener('mousedown', onClickOutside);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onClickOutside);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    const selectedPrice = selected ? getPrice(selected) : undefined;

    return (
        <div ref={rootRef} className="relative inline-block w-full max-w-[280px]">
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggle(); }}
                aria-haspopup="listbox"
                aria-expanded={open}
                className={`flex items-center justify-between gap-2 w-full pl-3 pr-2.5 py-2 rounded-lg border border-solid bg-white cursor-pointer transition-colors ${open ? 'border-orange-400 ring-2 ring-orange-100' : 'border-neutral-200 hover:border-neutral-300'}`}
            >
                <span className="flex items-baseline gap-1.5 min-w-0">
                    <span className="text-sm font-semibold text-neutral-800 truncate" style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}>
                        {selected?.name || 'Select plan'}
                    </span>
                    {selectedPrice > 0 && (
                        <span className="text-xs font-medium text-neutral-400 whitespace-nowrap">{formatInr(selectedPrice)}</span>
                    )}
                </span>
                <ChevronDown size={15} className={`text-neutral-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <ul
                    role="listbox"
                    aria-label="Select meal plan"
                    className="absolute z-30 left-0 right-0 mt-1.5 py-1.5 bg-white rounded-xl border border-solid border-neutral-100 shadow-lg max-h-64 overflow-y-auto"
                >
                    {plans.map((p) => {
                        const isSelected = selected?.id === p?.id;
                        const price = getPrice(p);
                        return (
                            <li key={p?.id} role="option" aria-selected={isSelected}>
                                <button
                                    type="button"
                                    onClick={() => { onSelect(p); closeBackEntry(); }}
                                    className={`w-full flex items-start justify-between gap-3 px-3.5 py-2.5 text-left cursor-pointer transition-colors ${isSelected ? 'bg-orange-50' : 'hover:bg-neutral-50'}`}
                                >
                                    <span className="flex flex-col gap-0.5 min-w-0">
                                        <span className="text-sm font-semibold text-neutral-800" style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}>
                                            {p?.name}
                                        </span>
                                        {p?.description && (
                                            <span className="text-xs text-neutral-400 leading-snug line-clamp-2">{p.description}</span>
                                        )}
                                    </span>
                                    <span className="flex items-center gap-2 flex-shrink-0 pt-0.5">
                                        {price > 0 && (
                                            <span className="text-xs font-semibold text-neutral-500 whitespace-nowrap">{formatInr(price)}</span>
                                        )}
                                        {isSelected && <Check size={15} strokeWidth={2.5} style={{ color: '#0F6E56' }} />}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

type Props = {
    assetGroup: TAssetGroup;
    loading?: "lazy" | "eager";
    startDate: string;
    endDate: string;
    roomCount: number;
    setSelectedAssetGroup?: (assetGroup: TAssetGroup) => void;
    openAppointmentForm?: () => void | Promise<unknown>;
    setSelectedPlan?: Function;
    maxRoomCount?: number;
    available?: TAssetGroupAvailability;
    bookingRequirement?: TBookingRequirement[];
    setBookingRequirement?: Function;
    setOpenDetailStay?: Function;
    store?: TStore
};

export const AssetGroupCard: React.FC<Props> = ({
    assetGroup, loading, startDate, endDate, roomCount,
    setSelectedAssetGroup, openAppointmentForm,
    maxRoomCount, available, bookingRequirement,
    setBookingRequirement, setOpenDetailStay, store
}: Props) => {
    const [plan, setPlan] = useState<TPricingPlan | null>(null);
    // Booking opens a server-rendered page, so the click is followed by a round-trip the guest
    // would otherwise see nothing for. Only reset on failure — on success this card unmounts.
    const [openingBooking, setOpeningBooking] = useState(false);
    // Plans the booking engine quoted for this room and party, named from the schedule response.
    const availablePlans = useMemo(
        () => getOfferedPlans(available, assetGroup?.prices),
        [available, assetGroup?.prices]
    );

    useEffect(() => {
        setPlan(availablePlans[0] || null);
    }, [availablePlans]);

    const maxAvailableRooms = useMemo(() => {
        if (!available?.periods?.length) return 0;
        let min = Infinity;
        for (const p of available.periods) {
            if (p.available_rooms < min) min = p.available_rooms;
        }
        return min === Infinity ? 0 : min;
    }, [available?.periods]);

    const changeQty = (value: number) => {
        let updated = [...(bookingRequirement || [])];
        const idx = updated.findIndex((br) => br?.category?.id === assetGroup?.id);
        if (value === 0) {
            if (idx !== -1) updated.splice(idx, 1);
        } else {
            const price = value * (assetGroup?.prices?.find((p) => p?.plan_id === plan?.id)?.price || assetGroup?.price);
            if (idx !== -1) {
                updated[idx] = { ...updated[idx], quantity: value, price };
            } else {
                updated.push({ category: assetGroup, quantity: value, plan, startDate, endDate, enable: true, price });
            }
        }
        setBookingRequirement(updated);
    };

    const handleSelectPlan = (planId: number) => {
        const selected = availablePlans.find((p) => p?.id === planId);
        if (!selected) return;
        setPlan(selected);
        let updated = [...(bookingRequirement || [])];
        const idx = updated.findIndex((br) => br?.category?.id === assetGroup?.id);
        if (idx !== -1) {
            updated[idx] = { ...updated[idx], plan: selected };
            setBookingRequirement(updated);
        }
    };

    const handleBookNow = async () => {
        if (openingBooking) return;
        setOpeningBooking(true);
        try {
            await openAppointmentForm?.();
        } catch {
            setOpeningBooking(false);
        }
    };

    const currentQty = bookingRequirement?.find((br) => br?.category?.id === assetGroup?.id)?.quantity || 0;
    // Backend prices are pre-tax; show the GST-inclusive nightly rate (matches Google's feed price).
    const gstOn = !!store?.storemeta?.functions?.gst_billing;
    // Price the searched itinerary from the per-date availability (what Google's price check compares
    // against). Until a plan is resolved (always the case in the server-rendered HTML), the default
    // plan is the first one availability returns — the same plan the card then selects.
    const pricedPlanId = plan?.id ?? availablePlans[0]?.id;
    const staticNightly = assetGroup?.prices?.find((p) => p?.plan_id === pricedPlanId)?.price || assetGroup?.price;
    // Units the searched party needs in THIS category (server works it out from adults + children
    // and the category's capacity; dorms are per bed). Falls back to the page-level suggestion.
    const partyUnits = Math.max(1, available?.rooms_needed || roomCount || 1);
    // Before anything is added, price what the party needs; afterwards, what's actually selected.
    const pricedUnits = currentQty || partyUnits;
    const partyAdults = available?.guest_count || 0;
    const partyChildren = available?.child_count || 0;
    // Same occupancy rules as the server (guest_capacity, max_guest_capacity, extra-guest prices).
    const occupancyRules = useMemo(
        () => getOccupancyRules(assetGroup?.info, (assetGroup as any)?.capacity || available?.guest_capacity),
        [assetGroup?.info, (assetGroup as any)?.capacity, available?.guest_capacity]
    );
    // Server quote first (exactly what checkout charges, incl. extra-adult / extra-child charges);
    // local calculation only if it's missing.
    const stayPricing = useMemo(
        () => pricingFromQuote(available?.quotes, pricedPlanId, pricedUnits,
                partyAdults > 0 ? { adults: partyAdults, children: partyChildren, rules: occupancyRules } : null)
            || getStayPricing(available?.periods, pricedPlanId, startDate, endDate, gstOn, staticNightly, pricedUnits),
        [available?.quotes, available?.periods, pricedPlanId, startDate, endDate, gstOn, staticNightly, pricedUnits, partyAdults, partyChildren, occupancyRules]
    );
    const selectedQuote = available?.quotes?.find((q) => q?.plan_id === pricedPlanId) || available?.quotes?.[0];
    // Extra-guest charges in the shown total (only when priced for the party's own room count).
    const extraGuestNote = selectedQuote && pricedUnits === selectedQuote.rooms
        && ((selectedQuote.extra_adults || 0) + (selectedQuote.extra_children || 0)) > 0
        ? `incl. ${[
            selectedQuote.extra_adults ? `${selectedQuote.extra_adults} extra adult${selectedQuote.extra_adults > 1 ? 's' : ''}` : '',
            selectedQuote.extra_children ? `${selectedQuote.extra_children} extra child${selectedQuote.extra_children > 1 ? 'ren' : ''}` : '',
          ].filter(Boolean).join(' + ')}`
        : '';
    const partyLabel = partyAdults > 0
        ? `${partyAdults} adult${partyAdults > 1 ? 's' : ''}${partyChildren > 0 ? `, ${partyChildren} child${partyChildren > 1 ? 'ren' : ''}` : ''}`
        : '';
    // Server says fewer units are free on some night than this party needs.
    const cannotFitParty = available?.can_accommodate === false;
    const maxOccupancy = available?.max_occupancy || assetGroup?.max_occupancy || (assetGroup as any)?.capacity;
    const displayPrice = stayPricing?.avgNightly || withRoomGst(staticNightly, gstOn);
    const nightsLabel = stayPricing ? `${stayPricing.nights} night${stayPricing.nights > 1 ? 's' : ''}` : '';
    const perNightLabel = `${stayPricing?.nights > 1 ? 'avg. ' : ''}${gstOn ? 'incl. taxes / night' : '/ night'}`;
    const isDorm = !!assetGroup?.info?.is_dorm;
    const cancellationSummary = getCancellationSummary(store?.storemeta?.settings?.cancellation_policy, startDate);
    const isUnavailable = maxRoomCount === 0;
    // The open meal-plan list overlaps the card below; without its own higher layer, that card's
    // positioned children (image button z-10, badges z-20) paint over the bottom of the list.
    const [planMenuOpen, setPlanMenuOpen] = useState(false);
    // "Add" selects what the party needs here (capped by what's free), so checkout's quantity —
    // and therefore its total — matches the stay total shown on the card.
    const initialRoomQty = Math.max(1, Math.min(partyUnits, maxAvailableRooms || partyUnits));
    const roomUnitLabel = isDorm ? 'Bed' : 'Room';

    // Stay total for the searched dates: total, then the base + taxes split (shown on desktop and mobile).
    const StayTotal = ({ align = 'end' }: { align?: 'start' | 'end' }) => stayPricing ? (
        <div className={`flex flex-col ${align === 'end' ? 'items-end text-right' : 'items-start text-left'} gap-0.5`} style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}>
            <span className="text-xs font-semibold text-neutral-700">
                {formatInr(stayPricing.total)} for {nightsLabel}{pricedUnits > 1 ? `, ${pricedUnits} ${roomUnitLabel.toLowerCase()}s` : ''}
            </span>
            {currentQty === 0 && partyLabel && (
                <span className="text-[12px] text-neutral-500">for {partyLabel}{extraGuestNote ? ` · ${extraGuestNote}` : ''}</span>
            )}
            {gstOn && stayPricing.tax > 0 ? (
                <span className="text-[11px] text-neutral-400">
                    {formatInr(stayPricing.base)} + {formatInr(Math.round(stayPricing.total) - Math.round(stayPricing.base))} taxes &amp; fees
                </span>
            ) : (
                <span className="text-[11px] text-neutral-400">No additional taxes or fees</span>
            )}
        </div>
    ) : null;
    const addRoomLabel = initialRoomQty > 1 ? `Add ${initialRoomQty} ${roomUnitLabel}s` : `Add ${roomUnitLabel}`;

    // Shared stepper UI used in both mobile bar and desktop panel
    const StepperControls = () => (
        <div className="flex items-center gap-1.5">
            {currentQty === 0 ? (
                <button
                    onClick={() => changeQty(initialRoomQty)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold transition-all hover:opacity-90 active:scale-[0.97] border-none cursor-pointer bg-business"
                    style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}
                    aria-label={addRoomLabel}
                >
                    <Plus size={15} />
                    {addRoomLabel}
                </button>
            ) : (
                <div className="flex items-center gap-1 bg-white rounded-lg border border-solid border-neutral-200 p-0.5">
                    <button
                        onClick={() => changeQty(Math.max(0, currentQty - 1))}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-100 transition-colors border-none bg-transparent cursor-pointer"
                        aria-label="Remove 1 room"
                    >
                        <Minus size={14} />
                    </button>
                    <span className="w-7 text-center text-sm font-bold text-neutral-800 select-none" style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}>
                        {currentQty}
                    </span>
                    <button
                        onClick={() => changeQty(Math.min(maxAvailableRooms, currentQty + 1))}
                        disabled={currentQty >= maxAvailableRooms}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-white transition-colors border-none cursor-pointer disabled:opacity-40 bg-business"
                        aria-label="Add 1 more room"
                    >
                        <Plus size={14} />
                    </button>
                </div>
            )}
        </div>
    );

    return (
        isUnavailable ?
            <></>
            :
            <article
                aria-label={`${assetGroup?.name} room type`}
                className={`relative ${planMenuOpen ? 'z-40' : ''} bg-white rounded-2xl border border-solid border-neutral-100 shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 hover:border-neutral-200 ${isUnavailable ? "opacity-60" : ""}`}
            >
                {/* ── Card body: image | info | right-panel (desktop 3-col) ── */}
                <div className="flex flex-col md:flex-row md:items-stretch">

                    {/* ── Image ── */}
                    <div className="relative h-52 md:h-auto md:w-64 lg:w-72 flex-shrink-0 overflow-hidden">
                        <button
                            onClick={() => { setSelectedAssetGroup(assetGroup); setOpenDetailStay(true); }}
                            className="absolute inset-0 w-full h-full z-10"
                            aria-label={`View details for ${assetGroup?.name}`}
                        />
                        <Image
                            loader={sanityIoImageLoader}
                            src={assetGroup?.details[0]?.url || "https://jhattse.com/public/consumer/placeholder/product_square.png"}
                            alt={`${assetGroup?.name} room photo`}
                            fill
                            className="object-cover transition-transform duration-500 hover:scale-105"
                            loading={loading}
                            sizes="(max-width: 768px) 100vw, 288px"
                        />
                        <div
                            className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium z-20 backdrop-blur-sm"
                            style={isUnavailable
                                ? { backgroundColor: 'rgba(148,30,23,0.5)', color: '#FFF' }
                                : { backgroundColor: 'rgba(50,114,81,0.4)', color: '#FFF' }}
                        >
                            {isUnavailable
                                ? <><XCircle className="w-3.5 h-3.5" /> Not Available</>
                                : <><CheckCircle2 className="w-3.5 h-3.5" /> {maxRoomCount} Left</>
                            }
                        </div>
                    </div>

                    {/* ── Middle: room name + features + meal plan ── */}
                    <div className="flex flex-col flex-1 p-4 gap-3 min-w-0">
                        <h3
                            className="text-lg font-bold text-neutral-900 capitalize leading-tight cursor-pointer hover:underline"
                            onClick={() => { setSelectedAssetGroup(assetGroup); setOpenDetailStay(true); }}
                            style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}
                        >
                            {assetGroup?.name?.toLocaleLowerCase()}
                        </h3>
                        {(maxOccupancy > 0 || isDorm) && (
                            <p className="flex items-center gap-1.5 text-sm font-medium text-neutral-600 m-0" style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}>
                                <Users size={13} className="flex-shrink-0 text-neutral-400" />
                                {isDorm
                                    ? `Shared dormitory · price is per bed${maxOccupancy > 0 ? ` · ${maxOccupancy} beds per room` : ''}`
                                    : `Up to ${occupancyRules.max} adult${occupancyRules.max > 1 ? 's' : ''} · rate includes ${occupancyRules.base} guest${occupancyRules.base > 1 ? 's' : ''}`
                                        + `${occupancyRules.extraAdultPrice > 0 && occupancyRules.max > occupancyRules.base ? ` · extra adult ${formatInr(occupancyRules.extraAdultPrice)}/night` : ''}`
                                        + `${occupancyRules.extraChildPrice > 0 ? ` · extra child ${formatInr(occupancyRules.extraChildPrice)}/night` : ''}`
                                        + ` · children under 12 don't count`}
                            </p>
                        )}
                        {cannotFitParty && (
                            <p className="flex items-start gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5 m-0" style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}>
                                <XCircle size={13} className="flex-shrink-0 mt-px" />
                                {`Only ${maxAvailableRooms} ${roomUnitLabel.toLowerCase()}${maxAvailableRooms === 1 ? '' : 's'} left — ${partyLabel || 'your group'} needs ${partyUnits}. Combine with another room type.`}
                            </p>
                        )}
                        <AmenitiesList amenityIds={assetGroup?.info?.amenities} />
                        {/* Cancellation terms for this rate, shown before the guest books. */}
                        <p className="flex items-start gap-1.5 text-xs text-neutral-600 m-0 leading-snug" style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}>
                            <CalendarX2 size={13} className="flex-shrink-0 mt-px text-neutral-400" />
                            <span>
                                {cancellationSummary}
                            </span>
                        </p>
                        {/* Meal plan selector — modern custom dropdown (name + per-night price) */}
                        {!isUnavailable && availablePlans.length > 0 && (
                            <div className="mt-auto">
                                <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1.5" style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}>
                                    Meal Plan
                                </span>
                                <MealPlanDropdown
                                    onOpenChange={setPlanMenuOpen}
                                    plans={availablePlans}
                                    selected={plan}
                                    getPrice={(p) => pricingFromQuote(available?.quotes, p?.id, 1)?.avgNightly
                                        || getStayPricing(available?.periods, p?.id, startDate, endDate, gstOn, assetGroup?.prices?.find((price) => price?.plan_id === p?.id)?.price, 1)?.avgNightly
                                        || withRoomGst(assetGroup?.prices?.find((price) => price?.plan_id === p?.id)?.price, gstOn)}
                                    onSelect={(p) => handleSelectPlan(p?.id)}
                                />
                            </div>
                        )}
                    </div>

                    {/* ── Right panel: price + stepper + book (DESKTOP ONLY) ── */}
                    <div className="hidden md:flex flex-col items-end justify-between gap-4 p-4 border-l border-solid border-neutral-100 bg-neutral-50 min-w-[180px]">
                        {/* Price */}
                        <div className="flex flex-col items-end gap-1">
                            {displayPrice > 0 ? (
                                <>
                                    <span className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}>
                                        {formatInr(displayPrice)}
                                    </span>
                                    <span className="text-xs text-neutral-400 font-medium">{perNightLabel}</span>
                                    <StayTotal align="end" />
                                </>
                            ) : (
                                <span className="text-sm text-neutral-400">Price on request</span>
                            )}
                        </div>

                        {/* Stepper + Book Now stacked */}
                        {isUnavailable ? (
                            <div className="flex items-center gap-1.5 text-sm text-neutral-500">
                                <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                                Unavailable
                            </div>
                        ) : (
                            <div className="flex flex-col items-end gap-2">
                                {/* Rooms label + stepper */}
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-neutral-400 uppercase tracking-wide" style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}>
                                        Rooms
                                    </span>
                                    <StepperControls />
                                </div>
                                {/* Availability hint */}
                                {currentQty > 0 && (
                                    <span className="text-xs text-neutral-400" style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}>
                                        {currentQty} of {maxAvailableRooms} available
                                    </span>
                                )}
                                {/* Book Now */}
                                {currentQty > 0 && (
                                    <button
                                        onClick={handleBookNow}
                                        disabled={openingBooking}
                                        aria-busy={openingBooking}
                                        className="w-full flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.97] border-none cursor-pointer bg-business disabled:opacity-70 disabled:cursor-wait disabled:active:scale-100"
                                        style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}
                                    >
                                        {openingBooking
                                            ? <><Loader size={15} className="animate-spin" /> Opening…</>
                                            : 'Book Now →'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Mobile: price row + bottom action bar ── */}
                <div className="md:hidden">
                    {/* Price row */}
                    <div className="flex items-center justify-between px-4 py-2 border-t border-solid border-neutral-100">
                        {displayPrice > 0 ? (
                            <>
                                <div className="flex items-baseline gap-1.5">
                                    <span className="text-xl font-bold text-neutral-900" style={{ fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}>
                                        {formatInr(displayPrice)}
                                    </span>
                                    <span className="text-xs text-neutral-400 font-medium">{perNightLabel}</span>
                                </div>
                                <StayTotal align="end" />
                            </>
                        ) : (
                            <span className="text-sm text-neutral-400">Price on request</span>
                        )}
                    </div>

                    {/* Action bar */}
                    <div className="flex items-center justify-between gap-3 px-4 py-3 bg-neutral-50 border-t border-solid border-neutral-100 rounded-b-2xl">
                        {isUnavailable ? (
                            <div className="flex items-center gap-2 text-sm text-neutral-500">
                                <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                                No rooms available for selected dates
                            </div>
                        ) : (
                            <>
                                <StepperControls />
                                {currentQty > 0 && (
                                    <button
                                        onClick={handleBookNow}
                                        disabled={openingBooking}
                                        aria-busy={openingBooking}
                                        className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.97] whitespace-nowrap border-none cursor-pointer disabled:opacity-70 disabled:cursor-wait disabled:active:scale-100"
                                        style={{ backgroundColor: '#0F6E56', fontFamily: 'var(--asset-card-font, Manrope, sans-serif)' }}
                                    >
                                        {openingBooking
                                            ? <><Loader size={15} className="animate-spin" /> Opening…</>
                                            : 'Book Now →'}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </article>
    );
};